/**
 * GaugeWidget - Display percentage/ratio values as circular gauges
 */

import React from "react";
import PropTypes from "prop-types";
import { Box, alpha } from "@mui/material";
import KDTypography from "components/KDTypography";
import BaseWidget from "./BaseWidget";
import { useMaterialUIController } from "context";
import { useDashboard } from "../context/DashboardContext";
import { getWidgetById } from "../config/widgetRegistry";

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split(".");
  let current = obj;
  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    current = current[key];
  }
  return current;
};

function CircularGauge({ value, max = 100, color, size = 100, thickness = 10, label, format = "percentage", darkMode = true }) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getValueColor = () => {
    if (format === "percentage" && label?.toLowerCase().includes("error")) {
      if (percentage <= 1) return "#10B981";
      if (percentage <= 5) return "#F59E0B";
      return "#EF4444";
    }
    if (format === "percentage" && label?.toLowerCase().includes("health")) {
      if (percentage >= 95) return "#10B981";
      if (percentage >= 80) return "#F59E0B";
      return "#EF4444";
    }
    return color;
  };

  const displayColor = getValueColor();

  const formatDisplayValue = () => {
    if (value === null || value === undefined) return "—";
    switch (format) {
      case "percentage":
        return `${value.toFixed(1)}%`;
      case "milliseconds":
        if (value < 1000) return `${Math.round(value)}ms`;
        return `${(value / 1000).toFixed(2)}s`;
      case "number":
        return value.toLocaleString();
      default:
        return value;
    }
  };

  return (
    <Box sx={{ position: "relative", width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"} strokeWidth={thickness} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={displayColor}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{
            transition: "stroke-dashoffset 0.5s ease, stroke 0.3s ease",
            filter: `drop-shadow(0 0 8px ${alpha(displayColor, 0.5)})`,
          }}
        />
      </svg>
      <Box sx={{ position: "absolute", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <KDTypography sx={{ color: darkMode ? "white" : "rgba(0,0,0,0.87)", fontWeight: 700, fontSize: size > 80 ? "1.25rem" : "1rem", lineHeight: 1 }}>
          {formatDisplayValue()}
        </KDTypography>
      </Box>
    </Box>
  );
}

CircularGauge.propTypes = {
  value: PropTypes.number,
  max: PropTypes.number,
  color: PropTypes.string,
  size: PropTypes.number,
  thickness: PropTypes.number,
  label: PropTypes.string,
  format: PropTypes.string,
};

function GaugeWidget({ widgetId, value: propValue, max: propMax, loading: propLoading, error: propError, isExpanded = false }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { metricsData } = useDashboard();
  const widget = getWidgetById(widgetId);

  if (!widget) return null;

  const categoryData = metricsData[widget.category];
  const dataValue = propValue ?? getNestedValue(categoryData, widget.dataKey) ?? getNestedValue(categoryData?.data, widget.dataKey);
  const loading = propLoading ?? categoryData === undefined;
  const error = propError ?? null;
  const max = propMax ?? (widget.format === "percentage" ? 100 : 100);

  return (
    <BaseWidget
      id={widgetId}
      title={widget.title}
      icon={widget.icon}
      color={widget.categoryColor}
      gradient={widget.categoryGradient}
      loading={loading}
      error={error}
      showDragHandle
      isExpanded={isExpanded}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
        <CircularGauge
          value={dataValue ?? 0}
          max={max}
          color={widget.categoryColor || "#7C3AED"}
          size={90}
          thickness={8}
          label={widget.title}
          format={widget.format}
          darkMode={darkMode}
        />
        <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", textAlign: "center", fontSize: "0.7rem" }}>
          {widget.description}
        </KDTypography>
      </Box>
    </BaseWidget>
  );
}

GaugeWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  value: PropTypes.number,
  max: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.shape({ message: PropTypes.string }),
  isExpanded: PropTypes.bool,
};

export default GaugeWidget;
